import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useFullscreen } from '@/hooks/useFullscreen';
import { cn } from '@/lib/utils';
import type { DicomJson } from '../data/dicomTags';
import { getDataSource } from '../data/dataSourceFactory';
import { selectEngine } from './engineSelector';
import { SlideBackdrop } from './SlideBackdrop';
import { viewStateFrames } from './viewStateFrames';
import type { EngineCapabilities, ViewportEngine, ViewState } from './ViewportEngine';
import { ViewportControls } from '../features/viewer-controls/ViewportControls';
import { isOverMinimap } from '../features/viewer-controls/OverviewMinimap';
import { OBJECTIVE_SCALE, pinchFactor, scaleAt } from './zoomLimits';
import type { ScanSession } from './scan/session';
import { CaptureLayer } from './capture/CaptureLayer';
import type { CaptureRect } from './capture/region';

// Part of the scan's chunk, loaded before a session can exist.
const CoverageLayer = lazy(() =>
  import('../features/viewer-controls/reviewScanModule').then((m) => ({ default: m.CoverageLayer })),
);
const DrawAreaLayer = lazy(() =>
  import('../features/viewer-controls/reviewScanModule').then((m) => ({ default: m.DrawAreaLayer })),
);

interface Props {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  /**
   * Undefined while it is still on its way. The backdrop needs only the two
   * UIDs, so it is already fetching and showing; the engine starts once this
   * arrives.
   */
  metadata: DicomJson[] | undefined;
  /** Receives the live engine instance (or null on teardown) — used by the
   *  compare view to mirror navigation across panes. */
  onEngineReady?: (engine: ViewportEngine | null) => void;
  /** Exposes this viewport's fullscreen toggle to the parent (e.g. the study
   *  header), or null on teardown. */
  onFullscreenToggleReady?: (toggle: (() => void) | null) => void;
  /** Handle document-level keyboard shortcuts (zoom ladder, fullscreen). Disabled
   *  for compare panes so two viewports don't both react to one keypress. */
  keyboardShortcuts?: boolean;
  /** Handle trackpad pinch zoom. Disabled for compare panes so a pinch doesn't
   *  zoom both viewports; the compare view routes pinch to one pane. */
  pointerZoom?: boolean;
  /**
   * This viewport is the TOP layer of an overlay stack, so everything that would
   * hide the pane beneath it is dropped: the opaque background, the poster
   * backdrop (which sits at the fit framing, not the aligned one), and the
   * in-viewport controls — whose buttons set `pointer-events-auto` individually,
   * so a `pointer-events: none` wrapper does not stop them swallowing clicks
   * meant for the base pane.
   */
  overlay?: boolean;
  /** The open review scan: its bar and overlays are drawn INSIDE this
   *  viewport (the fullscreen element), so they follow it into fullscreen. */
  scan?: ScanSession | null;
  /** A review scan whose code is still loading: its bar shows, analyzing. */
  scanPending?: boolean;
  /** The image is shown inverted (an X-ray's Invert): the minimap follows. */
  inverted?: boolean;
  /**
   * The camera is armed: the next drag on the slide chooses the rectangle to
   * snapshot (viewer/capture/CaptureLayer), and the scan's own draw layer
   * stands aside for it.
   */
  capture?: { onCapture: (rect: CaptureRect) => void } | null;
}

// Longest the backdrop may stand in for a slide that hasn't finished painting.
// Generous: on the slow links this exists for, a big pyramid legitimately takes
// several seconds, and pulling the backdrop early is exactly the empty-tile view
// we're avoiding. Interaction retires it sooner.
const BACKDROP_MAX_MS = 15_000;
// Engines that cannot report a first paint get this instead of the full ceiling.
// The ceiling assumes the backdrop is standing in for tiles still arriving; an
// engine with nothing to report has already finished, so holding the image over
// it for fifteen seconds shows a blurry thumbnail on top of the sharp original.
const BACKDROP_UNREPORTED_MS = 1_000;

const NO_CAPS: EngineCapabilities = {
  magnification: false,
  rotate: false,
  scaleBar: false,
  annotations: false,
};

export function Viewport({
  studyInstanceUID,
  seriesInstanceUID,
  metadata,
  onEngineReady,
  onFullscreenToggleReady,
  keyboardShortcuts = true,
  pointerZoom = true,
  overlay = false,
  scan = null,
  scanPending = false,
  inverted = false,
  capture = null,
}: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<ViewportEngine | null>(null);
  const onEngineReadyRef = useRef(onEngineReady);
  onEngineReadyRef.current = onEngineReady;
  const shortcutsRef = useRef(keyboardShortcuts);
  shortcutsRef.current = keyboardShortcuts;
  const pointerZoomRef = useRef(pointerZoom);
  pointerZoomRef.current = pointerZoom;
  const { ref: fsRef, toggle: toggleFullscreen, fullscreen } = useFullscreen<HTMLDivElement>();

  // Merge our wrapper ref with the fullscreen callback ref.
  const setWrapperRef = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el;
      fsRef(el);
    },
    [fsRef],
  );

  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // The pregenerated slide image covering the map until it has really painted.
  // Retired by whichever comes first: a complete paint, the user moving the view
  // (the backdrop doesn't follow it), or the timeout — a slide that never
  // finishes loading must not sit behind an image pretending it did.
  const [backdrop, setBackdrop] = useState(true);
  // Whether the backdrop is actually covering the map right now. While it is,
  // the viewport already shows the slide, so the spinner would be claiming a
  // load that the user can plainly see has happened.
  const [backdropCovering, setBackdropCovering] = useState(false);
  const [caps, setCaps] = useState<EngineCapabilities>(NO_CAPS);
  const [viewState, setViewState] = useState<ViewState | null>(null);
  // The engine as state, for children that mount against it (the scan's
  // on-slide layer); the ref above serves the handlers.
  const [liveEngine, setLiveEngine] = useState<ViewportEngine | null>(null);

  // The scan moves the view from outside the mount, so the retire listeners
  // below never see it; a poster over a moving slide would be the backdrop at
  // its worst. Taken down when the scan opens, as the minimap does for itself.
  useEffect(() => {
    if (scan) setBackdrop(false);
  }, [scan]);

  // A press on the slide pauses a running scan, even one that moves nothing
  // (a click to look, the start of a drag). Moves already pause it; this is
  // for the click that does not move the view.
  const scanController = scan?.controller ?? null;
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !scanController) return;
    const pause = (e: PointerEvent) => {
      if (e.button === 0 || e.pointerType === 'touch') scanController.pause('user');
    };
    mount.addEventListener('pointerdown', pause, { capture: true });
    return () => mount.removeEventListener('pointerdown', pause, { capture: true });
  }, [scanController]);

  // The backdrop's ceiling and its retirement on interaction run from the
  // moment the slide is mounted, not from when its metadata lands: until then
  // the poster is all there is, and on a slow answer (the database waking can
  // take 15–30 s) it must not stand there as a slide that ignores the hand.
  // Taken down, it shows the spinner beneath.
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    setBackdrop(true);
    const retireBackdrop = () => setBackdrop(false);
    const backdropTimeout = setTimeout(retireBackdrop, BACKDROP_MAX_MS);
    for (const ev of ['pointerdown', 'wheel'] as const) {
      mount.addEventListener(ev, retireBackdrop, { passive: true });
    }
    return () => {
      clearTimeout(backdropTimeout);
      for (const ev of ['pointerdown', 'wheel'] as const) {
        mount.removeEventListener(ev, retireBackdrop);
      }
    };
  }, [studyInstanceUID, seriesInstanceUID]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !metadata || metadata.length === 0) return;

    // Each effect run renders into its OWN host element so a stale teardown can't
    // wipe the live engine's layers.
    const host = document.createElement('div');
    host.style.width = '100%';
    host.style.height = '100%';
    mount.appendChild(host);

    let engine: ViewportEngine | null = null;
    let disposed = false;
    let observer: ResizeObserver | null = null;
    let unsubscribe: (() => void) | null = null;

    setReady(false);
    setError(null);
    setViewState(null);
    setCaps(NO_CAPS);

    const retireBackdrop = () => setBackdrop(false);
    // The controls follow the view once a frame, not on every pointer event (viewStateFrames).
    const viewFrames = viewStateFrames(setViewState);
    let unsubscribeFirstPaint: (() => void) | null = null;
    let unreportedTimeout: ReturnType<typeof setTimeout> | undefined;
    // A plain scroll zooms through the engine's scroll gesture (scrollZoom),
    // which OpenLayers' own wheel zoom is turned off for. A pinch (Ctrl+wheel)
    // is zoomed by the page-level handler below, or by the compare view.
    const onScroll = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      e.preventDefault();
      engine?.scrollZoom(e, { x: e.clientX, y: e.clientY });
    };
    mount.addEventListener('wheel', onScroll, { capture: true, passive: false });

    (async () => {
      try {
        const selected = await selectEngine(metadata);
        if (disposed) return;
        await selected.init(host, {
          client: getDataSource().rawClient(),
          studyInstanceUID,
          seriesInstanceUID,
          metadata,
        });
        if (disposed) {
          selected.destroy();
          return;
        }
        engine = selected;
        engineRef.current = selected;
        setLiveEngine(selected);
        setCaps(selected.capabilities);
        unsubscribe = selected.subscribe(viewFrames.push);
        observer = new ResizeObserver(() => engine?.resize());
        observer.observe(host);
        setReady(true);
        // An engine that can't report a first paint gets a short grace period
        // instead of the long ceiling — see BACKDROP_UNREPORTED_MS.
        unsubscribeFirstPaint = selected.onFirstPaint?.(retireBackdrop) ?? null;
        if (!unsubscribeFirstPaint) {
          unreportedTimeout = setTimeout(retireBackdrop, BACKDROP_UNREPORTED_MS);
        }
        onEngineReadyRef.current?.(selected);
      } catch (e) {
        if (!disposed) {
          setError((e as Error).message);
          setBackdrop(false);
        }
      }
    })();

    return () => {
      disposed = true;
      clearTimeout(unreportedTimeout);
      unsubscribeFirstPaint?.();
      mount.removeEventListener('wheel', onScroll, { capture: true });
      unsubscribe?.();
      viewFrames.stop();
      observer?.disconnect();
      try {
        engine?.destroy();
      } catch {
        // best-effort
      }
      if (engineRef.current === engine) engineRef.current = null;
      setLiveEngine((live) => (live === engine ? null : live));
      onEngineReadyRef.current?.(null);
      host.remove();
    };
  }, [studyInstanceUID, seriesInstanceUID, metadata]);

  // Minimap image: the pipeline's thumbnail, or no minimap. 512px is ample for
  // a 140px box, and it's the same object the backdrop already fetched, so the
  // minimap costs nothing. The engine used to stitch its own from up to 64 tile
  // fetches, which put image assembly back in the client and could disagree
  // with what the viewport showed.
  const getSlideThumbnail = useCallback(async (): Promise<{
    url: string;
    aspect: number;
  } | null> => {
    const url = getDataSource().seriesThumbnailUrl(studyInstanceUID, seriesInstanceUID);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        return { url, aspect: img.naturalWidth / img.naturalHeight };
      }
    } catch {
      // 404 (no preview built yet) or an undecodable body — no minimap.
    }
    return null;
  }, [studyInstanceUID, seriesInstanceUID]);

  // Re-fit the engine when entering/leaving fullscreen (the container resizes).
  useEffect(() => {
    requestAnimationFrame(() => engineRef.current?.resize());
  }, [fullscreen]);

  // Expose the fullscreen toggle to the parent (study header button).
  const fsReadyRef = useRef(onFullscreenToggleReady);
  fsReadyRef.current = onFullscreenToggleReady;
  useEffect(() => {
    fsReadyRef.current?.(toggleFullscreen);
    return () => fsReadyRef.current?.(null);
  }, [toggleFullscreen]);

  // Keyboard: digits 1..9,0 = zoom ladder (1 = whole image, 0 = the top of the lens's scale), F = fullscreen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!shortcutsRef.current) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return;
      }
      const engine = engineRef.current;
      if (!engine) return;
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        engine.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        engine.zoomOut();
      } else if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        // A held digit repeats about 30 times a second toward the same rung:
        // nothing to gain. (+ and - keep repeating: holding them zooms on.)
        if (e.repeat) return;
        const d = Number(e.key);
        if (d === 1) {
          engine.fit(); // 1 = fit to screen (whole slide)
        } else {
          // Ladder 2,3,…,9,0 (0 wraps to the top): step 0..8.
          const step = d === 0 ? 8 : d - 2;
          if (engine.capabilities.magnification) {
            // Log-spaced over the lens's scale (1× to 100× on a slide); 0 = the top.
            engine.setMagnification(scaleAt(engine.capabilities.zoomScale ?? OBJECTIVE_SCALE, step / 8));
          } else {
            engine.zoomToFraction(step / 8); // fallback for non-magnification engines
          }
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggleFullscreen]);

  // Trackpad pinch defaults to browser page zoom (Chrome/FF emit ctrl+wheel,
  // Safari emits gesture* events). Intercept in the CAPTURE phase at the document
  // (while the study page is mounted): always preventDefault so the page never
  // zooms, and when the pinch is over the viewport, stopPropagation and drive the
  // engine's anchored zoom ourselves (OpenLayers' own wheel handling is
  // unreliable for real pinch, so we don't depend on it).
  useEffect(() => {
    const onWheelCapture = (e: WheelEvent) => {
      if (!pointerZoomRef.current) return; // compare panes route pinch themselves
      if (!e.ctrlKey) return; // pinch only; leave normal scroll/zoom to the engine
      e.preventDefault();
      if (isOverMinimap(e)) return; // it zooms about the spot it points at
      const engine = engineRef.current;
      if (!engine) return;
      // Pinch anywhere on the study page zooms the image: anchored at the cursor
      // when over the viewport, at the image center when over the surrounding UI.
      // "Over" is by position, not by the DOM: the annotation overlay is laid on
      // top of the viewport from outside it, and a pinch over a shape is a pinch
      // over the slide.
      const wrap = wrapperRef.current;
      // Measured only when the DOM cannot answer: a pinch sends an event a
      // frame, and the overlay repaints on each of them.
      const inRect = (r: DOMRect) =>
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      const overViewport =
        !!wrap && ((e.target instanceof Node && wrap.contains(e.target)) || inRect(wrap.getBoundingClientRect()));
      if (overViewport) {
        // Stopped here, it never reaches the mount's own retire listener.
        e.stopPropagation();
        setBackdrop(false);
      }
      engine.zoomByFactor(
        pinchFactor(e),
        overViewport ? { x: e.clientX, y: e.clientY } : undefined,
      );
    };
    const onGesture = (e: Event) => {
      if (pointerZoomRef.current) e.preventDefault();
    };
    document.addEventListener('wheel', onWheelCapture, { capture: true, passive: false });
    document.addEventListener('gesturestart', onGesture, { passive: false });
    document.addEventListener('gesturechange', onGesture, { passive: false });
    document.addEventListener('gestureend', onGesture, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheelCapture, { capture: true });
      document.removeEventListener('gesturestart', onGesture);
      document.removeEventListener('gesturechange', onGesture);
      document.removeEventListener('gestureend', onGesture);
    };
  }, []);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <div className="max-w-lg rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <div className="mb-1 font-semibold">Cannot display this series</div>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setWrapperRef}
      // overflow-hidden is load-bearing: OpenLayers grows its canvas beyond the
      // viewport box once the view is rotated, so that the rotated map still
      // covers it. Unclipped, that oversized canvas spills over whatever sits
      // around the viewport — after rotating a slide it covered the toolbar
      // above and swallowed every click on it, while controls stacked above the
      // map kept working, which made it look like the buttons had died.
      className={cn('relative h-full w-full overflow-hidden', !overlay && 'bg-viewport')}
    >
      {!ready && !backdropCovering && !overlay && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-white/70" />
        </div>
      )}
      {/* ph-no-capture: a pan or a polygon's clicks are not analytics (lib/analytics.ts). */}
      <div ref={mountRef} className="ph-no-capture h-full w-full" />
      {!overlay && (
        <SlideBackdrop
          studyInstanceUID={studyInstanceUID}
          seriesInstanceUID={seriesInstanceUID}
          visible={backdrop}
          onCovering={setBackdropCovering}
        />
      )}
      {ready && !overlay && scan?.onImage && liveEngine && (
        <Suspense fallback={null}>
          <CoverageLayer engine={liveEngine} scan={scan.controller} />
        </Suspense>
      )}
      {ready && !overlay && scan?.drawing && !capture && liveEngine && (
        <Suspense fallback={null}>
          <DrawAreaLayer
            engine={liveEngine}
            onDraw={scan.addArea}
            // A drag on the slide pauses a running scan, as a press does.
            onDrawStart={() => scan.controller.pause('user')}
          />
        </Suspense>
      )}
      {ready && !overlay && capture && liveEngine && (
        <CaptureLayer
          engine={liveEngine}
          onCapture={capture.onCapture}
          onDrawStart={() => scan?.controller.pause('user')}
        />
      )}
      {ready && !overlay && (
        <ViewportControls
          viewState={viewState}
          capabilities={caps}
          isFullscreen={fullscreen}
          scan={scan}
          scanPending={scanPending}
          inverted={inverted}
          onZoomIn={() => engineRef.current?.zoomIn()}
          onZoomOut={() => engineRef.current?.zoomOut()}
          onFit={() => engineRef.current?.fit()}
          onToggleFullscreen={toggleFullscreen}
          onSetMagnification={(m) => engineRef.current?.setMagnification(m)}
          onPreviewAt={(m) => engineRef.current?.previewAt(m) ?? Promise.resolve(null)}
          onGetThumbnail={getSlideThumbnail}
          // The minimap moves the view from outside the mount, so it retires
          // the backdrop itself, as any interaction does.
          onNavigate={(x, y, animate) => {
            setBackdrop(false);
            engineRef.current?.navigateToFraction(x, y, animate);
          }}
          onZoomAt={(factor, x, y) => {
            setBackdrop(false);
            engineRef.current?.zoomAtFraction(factor, x, y);
          }}
          onScrollAt={(e, x, y) => {
            setBackdrop(false);
            engineRef.current?.scrollZoomAtFraction(e, x, y);
          }}
        />
      )}
    </div>
  );
}
